import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Space, Input, Form, Typography, Tag, Switch, Row, Col, Tooltip, } from 'antd';
import { PlusOutlined, SearchOutlined, FolderOpenOutlined, RightOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import Paginations from '../../components/pagination';
import { categoryActions } from '../../actions/categoryActions';
import FilterPopover from '../../components/filter';
import ConfirmModal from '../../components/confirmModal';
import ServiceFormModal from '../../components/modals/serviceFormModal';
import { commonActions } from '../../actions/commonActions';
import { commonService } from '../../services/commonService';
import renderRequiredDocuments from '../../components/document';
import TableData from '../../components/table';
import GuardedButton from '../../components/guardedButton';
import ServiceDetailPanel from './serviceDetailPanel';
import { useCurrentPageAccess } from '../../services/helper';
import SpinnerLoader from '../../components/spinnerLoader';

const { Text } = Typography;

export interface FieldSpec {
  name: string;
  label: string;
  path?: string;
  type?: 'text' | 'email' | 'phone' | 'date' | 'textarea' | 'tags' | 'dropdown';
  editable?: boolean;
  rules?: any[];
  placeholder?: string;
  options?: Array<{ label: string; value: any; disabled?: boolean }>;
  formatter?: (value: any, item: any) => React.ReactNode;
}

interface serviceProps {
  role?: string;
}

interface QueryService {
  key: string;
  shortName?: string;
  _id?: string;
  name: string | undefined;
  requiredDocuments?: string[];
  isActive: boolean;
  serviceName?: string;
  categoryId?: string;
  type?: string;
  createdByUser?: string;
  price?: number;
  category?: string;
  categoryType?: string;
  createdAt?: string;
  description?: string;
  status: 'ENABLED' | 'DISABLED';
}

interface categoryParams {
  _id?: string;
  categoryName?: string;
  shortName?: string;
  categoryType?: string;
}

interface ApiService {
  _id: string;
  shortName?: string;
  name: string;
  price?: number;
  requiredDocuments?: string[];
  isActive: boolean;
  serviceName?: string;
  type: string;
  createdBy?: string;
  createdByUser?: string;
  category?: categoryParams;
  status?: string;
  createdAt?: string;
  description?: string;
}


const Service: React.FC<serviceProps> = ({ role }) => {

  const hasFetchedInitialData = useRef(false);
  const mountCount = useRef(0);
  const [categories, setCategories] = useState<QueryService[]>([]);
  const pageAccess = useCurrentPageAccess();
  const categoryState = useSelector((state: any) => state.getCategoryReducer);
  const categoryOptions = useMemo(() => {

    const payload = categoryState?.categorydata;
    const list = payload?.categories ?? payload ?? [];
    return (Array.isArray(list) ? list : []).map((c: any) => ({
      label: c?.categoryName,
      value: c?._id,
    }));
  }, [categoryState]);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [filterData, setIsFilterData] = useState({ categoryId: "", isActive: "", minPrice: "", maxPrice: "" });
  const [isFilterData, setIsFilter] = useState(false);
  const dispatch = useDispatch();
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [mutableCategory, setMutableCategory] = useState<any[]>([]);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [requiredDocuments, setRequiredDocuments] = useState<string[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const defaultPayLoad = { page: 1, limit: 10, };
  const [visible, setVisible] = useState(false);
  const [pageSize, setPageSize] = useState<number>(Number(defaultPayLoad.limit));
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getService = useSelector((state: any) => state.getServiceParamsReducer);
  const serviceData = useSelector((state: any) => state.createServiceReducer);
  const deleteService = useSelector((state: any) => state.deleteServiceReducer);
  const serviceViewState = useSelector((state: any) => state.getServiceViewReducer);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (debouncedSearchText) {
      setCurrentPage(1);
    }
  }, [debouncedSearchText]);

  useEffect(() => {
    if (hasFetchedInitialData.current) return;
    hasFetchedInitialData.current = true;
    dispatch(categoryActions.getCategory({ isActive: true }));
  }, [dispatch]);


  useEffect(() => {

    mountCount.current += 1;
    if (mountCount.current === 2 && currentPage === 1 && !debouncedSearchText?.trim()) {
      return;
    }

    const payload: any = {
      pageNumber: currentPage,
      pageSize
    };
    if (debouncedSearchText) {
      payload.searchText = debouncedSearchText;
    }
    if (isFilterData && filterData && typeof filterData === 'object') {
      Object.entries(filterData).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).trim() !== '') {
          payload[k] = v;
        }
      });
    }
    dispatch(commonActions.getServiceParams(payload));
  }, [dispatch, currentPage, pageSize, debouncedSearchText, isFilterData, filterData]);

  // Collapse any open detail panel whenever the result set changes underneath it.
  useEffect(() => {
    setExpandedKey(null);
    setIsEditMode(false);
  }, [currentPage, pageSize, debouncedSearchText, isFilterData, filterData]);

  useEffect(() => {
    if (serviceData && serviceData?.isSuccess) {
      setIsModalOpen(false);
      setIsEditMode(false);
      setRequiredDocuments([]);
      dispatch(commonActions.getServiceParams({ pageNumber: currentPage, pageSize: pageSize }));
      if (expandedKey) {
        dispatch(commonActions.getServiceView({ id: expandedKey }));
      }
      serviceData.isSuccess = false;
    }
  }, [serviceData, currentPage, pageSize, dispatch, expandedKey]);

  useEffect(() => {
    if (deleteService && deleteService.isSuccess) {
      setVisible(false);
      setRecordToDelete('');
      setExpandedKey(null);
      setIsEditMode(false);
      dispatch(commonActions.getServiceParams({ pageNumber: currentPage, pageSize: pageSize }));
      deleteService.isSuccess = false;
    }
  }, [deleteService, currentPage, pageSize, dispatch]);


  const apiCategories: ApiService[] = useMemo(() => {
    const data = getService?.servicedata;
    if (!data) return [];
    return Array.isArray(data?.services) ? data.services : [];
  }, [getService?.servicedata]);


  useEffect(() => {

    if (serviceData?.isLoading) return;

    if (getService?.isLoading) return;

    const mapped: QueryService[] = apiCategories.map((c: ApiService) => ({
      key: c._id,
      name: c.serviceName,
      shortName: c.shortName,
      requiredDocuments: c.requiredDocuments,
      isActive: c.isActive ?? (c.status === 'ENABLED'),
      category: c?.category?.categoryName,
      categoryId: c?.category?._id,
      price: c?.price,
      type: c?.type,
      categoryType: c?.category?.categoryType,
      createdAt: c?.createdAt,
      createdByUser: c?.createdByUser,
      status: c.isActive ? 'ENABLED' : 'DISABLED',
    }));

    if (mapped.length > 0) {
      setMutableCategory(mapped);
    }else{
      setMutableCategory([]);
    }
  }, [apiCategories, getService?.isLoading, serviceData?.isLoading]);

  const handleOnPageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize) setPageSize(newPageSize);
  };


  const onFinish = (values: any) => {
    dispatch(commonActions.createService(values));
  };

  const toggleStatus = async (cat: any, enabled: boolean) => {

    const id = cat._id ?? cat.key ?? cat.id;
    setMutableCategory(prev => prev.map((c: any) => {
      const cId = c._id ?? c.key ?? c.id;
      if (String(cId) === String(id)) {
        return {
          ...c,
          isActive: !!enabled,
          status: enabled ? 'ENABLED' : 'DISABLED',
          loading: true,
        };
      }
      return c;
    }));

    setCategories(prev => prev.map((c: any) => (String(c.key) === String(id) ?
      { ...c, isActive: !!enabled, status: enabled ? 'ENABLED' : 'DISABLED' } : c)));

    try {

      await commonService.toggleService({
        _id: id,
        serviceName: cat.serviceName ?? cat.name,
        shortName: cat.shortName,
        categoryId: cat.categoryId,
        price: cat.price,
        type: cat.type,
        requiredDocuments: cat.requiredDocuments,
        isActive: enabled
      });

      setMutableCategory(prev => prev.map((c: any) => {
        const cId = c._id ?? c.key ?? c.id;
        if (String(cId) === String(id)) {
          const copy = { ...c };
          delete copy.loading;
          return copy;
        }
        return c;
      }));
    } catch (err: any) {
      setMutableCategory(prev => prev.map((c: any) => {
        const cId = c._id ?? c.key ?? c.id;
        if (String(cId) === String(id)) {
          return {
            ...c,
            isActive: !enabled,
            status: !enabled ? 'ENABLED' : 'DISABLED',
            loading: false,
          };
        }
        return c;
      }));
      setCategories(prev => prev.map((c: any) => (String(c.key) === String(id) ? { ...c, isActive: !enabled, status: !enabled ? 'ENABLED' : 'DISABLED' } : c)));
    }
  };



  const handleApplyFilter = (values: any) => {
    setIsFilter(true);
    setCurrentPage(1);

    const newFilterData: any = { categoryId: "", isActive: "", minPrice: "", maxPrice: "" };
    const appliedFiltersTemp: any = {};

    if (values?.categoryId) {
      newFilterData.categoryId = values.categoryId;
      appliedFiltersTemp.categoryId = values.categoryId;
    }
    if (values?.status !== undefined && values?.status !== null) {
      newFilterData.isActive = values.status;
      appliedFiltersTemp.isActive = values.status;
    }
    if (values?.priceRange?.length === 2) {
      const [min, max] = values.priceRange;
      newFilterData.minPrice = min;
      newFilterData.maxPrice = max;
      appliedFiltersTemp.minPrice = min;
      appliedFiltersTemp.maxPrice = max;
    }
    setIsFilterData(newFilterData);
    setAppliedFilters(appliedFiltersTemp);

  };

  const handleClearFilter = () => {
    setCurrentPage(1);
    setIsFilter(false);
    setIsFilterData({ categoryId: "", isActive: "", minPrice: "", maxPrice: "", });
    setAppliedFilters({});
  };



  const handleDelete = () => {
    dispatch(commonActions.deleteService(recordToDelete));
  }

  // ── Inline master-detail handlers ─────────────────────────────
  const handleExpand = (expanded: boolean, record: any) => {
    setIsEditMode(false);
    editForm.resetFields();
    setRequiredDocuments([]);
    if (expanded) {
      setExpandedKey(String(record.key));
      dispatch(commonActions.getServiceView({ id: record.key }));
    } else {
      setExpandedKey(null);
    }
  };

  const handlePanelEdit = (detail: any) => {
    editForm.setFieldsValue({
      serviceName: detail?.serviceName ?? detail?.name,
      shortName: detail?.shortName,
      price: detail?.price,
      type: detail?.type,
      categoryId: detail?.category?._id ?? detail?.categoryId,
    });
    setRequiredDocuments(Array.isArray(detail?.requiredDocuments) ? [...detail.requiredDocuments] : []);
    setIsEditMode(true);
  };

  const handlePanelCancel = () => {
    setIsEditMode(false);
    editForm.resetFields();
    setRequiredDocuments([]);
  };

  const handlePanelSave = (detail: any) => {
    editForm.validateFields().then((values) => {
      dispatch(commonActions.createService({
        ...values,
        _id: detail?._id ?? detail?.key,
        requiredDocuments,
      }));
    });
  };

  const handleDocAdd = (value: string) => {
    const v = value.trim();
    if (!v) return;
    setRequiredDocuments(prev => (prev.includes(v) ? prev : [...prev, v]));
  };
  const handleDocRemove = (index: number) =>
    setRequiredDocuments(prev => prev.filter((_, i) => i !== index));

  const columns = [
    {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 240,
    ellipsis: {
      showTitle: false,
    },
      render: (text: string) => (
        <Tooltip title={text || '-'}>
        <Space>
          <FolderOpenOutlined style={{ color: '#4f46e5' }} />
          <Text strong>{text}</Text>
        </Space>
        </Tooltip>
      ),
    },
   {
    title: 'Short Name',
    dataIndex: 'shortName',
    key: 'shortName',
    width: 130,
    ellipsis: {
      showTitle: false,
    },
      render: (d: string) =>   <Tooltip title={d || '-'}>
        <Text type="secondary" ellipsis>{d || '—'}</Text>
      </Tooltip>,
    },
      {
    title: 'Category Type',
    dataIndex: 'category',
    key: 'category',
    width: 170,
    ellipsis: {
      showTitle: false,
    },
      render: (d: string) => <Tooltip title={d || '-'}><Text type="secondary" ellipsis>{d || '—'}</Text></Tooltip>
    },
    {
    title: 'Created At',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
      render: (text: string, record: QueryService) => (
        <Tooltip
        title={
          record.createdAt
            ? new Date(record.createdAt).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
            : '-'
        }
      >
        <Text type="secondary" className="text-small" ellipsis>
          {record.createdAt
            ? new Date(record.createdAt).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
            : '-'}
        </Text>
      </Tooltip>
      ),
    },
     {
    title: 'Created By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 180,
    ellipsis: {
      showTitle: false,
    },
    render: (_: any, record: QueryService) => (
      <Tooltip title={record.createdByUser || '-'}>
        <Text ellipsis>{record.createdByUser || '-'}</Text>
      </Tooltip>
    ),
  },
  ];

  const filterFields = useMemo(() => {
    return [
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Select Status',
        options: [
          { label: 'Active', value: true },
          { label: 'Inactive', value: false },
        ],
      },
      {
        name: 'categoryId',
        label: 'Category',
        path: 'category._id',
        type: 'select',
        editable: true,
        rules: [{ required: true, message: 'Please select category' }],
        placeholder: 'Select category',
        options: categoryOptions,
        formatter: (_: any, item: any) => <>{item?.category?.categoryName ?? '-'}</>,
      },
      {
        name: 'priceRange',
        label: 'Price Range',
        type: 'slider',
        placeholder: 'Select Price Range',
        options: [
          { label: 'Service', value: 'SERVICE' },
          { label: 'Query', value: 'QUERY' },
        ],
      }
    ];
  }, [categoryOptions]);

  const canEdit = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;
  const canDelete = (role === 'ADMIN' || role === 'SUPER_ADMIN');

  // Merge full detail (from the view endpoint) with the live row's volatile fields.
  const viewService = (serviceViewState?.isSuccess && String(serviceViewState?.serviceview?.service?._id) === String(expandedKey))
    ? serviceViewState.serviceview.service
    : null;
  const detailLoading = Boolean(serviceViewState?.isLoading) && !viewService;

  const renderExpanded = (record: any) => {
    const detail = viewService
      ? { ...viewService, isActive: record.isActive }
      : {
        _id: record.key,
        serviceName: record.name,
        shortName: record.shortName,
        price: record.price,
        type: record.type,
        category: { categoryName: record.category, categoryType: record.categoryType, _id: record.categoryId },
        requiredDocuments: record.requiredDocuments,
        isActive: record.isActive,
        createdAt: record.createdAt,
        createdByUser: record.createdByUser,
      };
    return (
      <ServiceDetailPanel
        detail={detail}
        loading={detailLoading}
        isEditing={isEditMode}
        canEdit={canEdit}
        canDelete={canDelete}
        saving={Boolean(serviceData?.isLoading)}
        statusLoading={Boolean(record?.loading)}
        form={editForm}
        categoryOptions={categoryOptions}
        documents={requiredDocuments}
        onDocAdd={handleDocAdd}
        onDocRemove={handleDocRemove}
        onEdit={() => handlePanelEdit(detail)}
        onCancel={handlePanelCancel}
        onSave={() => handlePanelSave(detail)}
        onDelete={() => { setRecordToDelete(detail?._id ?? record.key); setVisible(true); }}
        onToggleStatus={(checked) => toggleStatus(record, checked)}
      />
    );
  };

  useEffect(() => {
    getService.isSuccess = false;
  }, []);

  useEffect(() => {
    if (getService.isSuccess || getService.isError) {
      setInitialLoaded(true);
    }
  }, [getService.isSuccess, getService.isError]);

  if (!initialLoaded && getService.isLoading) {
    return <SpinnerLoader />;
  }


  return (
    <div className="services-page">
      <Row justify="space-between" align="middle" className="mb-4 category-header">
        <div className="category-toolbar">
          <Input className='service-search-text' placeholder="Search by name or description" prefix={<SearchOutlined />}
            allowClear value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          <FilterPopover appliedCount={Object.keys(appliedFilters).length} fields={filterFields} onApply={handleApplyFilter} onClear={handleClearFilter} />
        </div>
        {(role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isAdd && <Col className='button-col'> <GuardedButton type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>Add Service</GuardedButton> </Col>}
      </Row>
      {getService?.isLoading && <SpinnerLoader />}
      <div className='driveMynt-card'>
        <TableData
          className='service-table service-table--expandable table-scroll'
          columns={columns}
          rowKey={(record: any) => String(record.key)}
          dataSource={mutableCategory}
          pagination={false}
          expandable={{
            expandedRowKeys: expandedKey ? [expandedKey] : [],
            onExpand: handleExpand,
            expandedRowRender: renderExpanded,
            expandRowByClick: pageAccess.isView,
            rowExpandable: () => pageAccess.isView,
            showExpandColumn: pageAccess.isView,
            columnWidth: 48,
            expandIcon: ({ expanded, onExpand, record }: any) => (
              <span
                className={`cat-expand-ic${expanded ? ' cat-expand-ic--open' : ''}`}
                onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
              >
                <RightOutlined />
              </span>
            ),
          }}
          onRow={(record: any) => ({
            className: String(record.key) === String(expandedKey) ? 'cat-row-active' : '',
            style: { cursor: pageAccess.isView ? 'pointer' : 'default' },
          })}
        />
        <Paginations total={getService?.servicedata?.pagination?.totalRecords} defaultCurrent={currentPage} defaultPageSize={pageSize}
          callback={handleOnPageChange} />
      </div>
      <ServiceFormModal
        open={isModalOpen}
        title={editing ? 'Edit Service' : 'Add New Service'}
        onCancel={() => { setIsModalOpen(false); form.resetFields(); }}
        onFinish={onFinish}
        form={form}
        isLoading={Boolean(serviceData?.isLoading)}
      />
      <ConfirmModal centered content="Are you sure you want to delete this service?" visible={visible}
        cancelText='cancel' okText='ok' title='Confirm Delete' onOk={handleDelete} onCancel={() => setVisible(false)} />
    </div>
  );
};
export default Service;
